import { PopperElement, Props } from 'tippy.js';
import { getData, postData } from '../utils/ajax-utils';
import { MemberAreaModalHTMLElement } from './member-area-modal';

export function initAcmeViewAccount() {
    const $viewAccountModal = document.querySelector<MemberAreaModalHTMLElement>('.js-account-view-modal');

    if ($viewAccountModal) {
        new AcmeViewAccount($viewAccountModal);
    }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface InitializableHTMLElement<T> extends HTMLElement {
    viewAccountInitialized?: boolean;
}

type AccountFieldType = 'refExt' | 'name' | 'domains' | 'status' | 'key';

type ButtonAttrType = {
    $button: HTMLButtonElement,
    refExt: string,
    name: string|null,
    domains: string|null,
    status: string|null,
    key: string|null,
};

class AcmeViewAccount {
    viewModalAttr: {
        $modal: MemberAreaModalHTMLElement,
        $detailContainer: {
            name: HTMLElement|null,
            domains: HTMLElement|null,
            status: HTMLElement|null,
            refExt: HTMLElement|null,
            key: HTMLElement|null,
        },
        $actionsContainer: HTMLElement|null,
        $regenKeyBtn: HTMLButtonElement|null,
        fetchErrorMessage: string,
    };
    buttonsAttr: ButtonAttrType[] = [];
    openedButtonAttr: ButtonAttrType|null = null;

    constructor($viewAccountModal: MemberAreaModalHTMLElement) {
        this.viewModalAttr = {
            $modal: $viewAccountModal,
            $detailContainer: {
                name: $viewAccountModal.querySelector('[data-field="name"]'),
                domains: $viewAccountModal.querySelector('[data-field="domains"]'),
                status: $viewAccountModal.querySelector('[data-field="status"]'),
                refExt: $viewAccountModal.querySelector('[data-field="ref_ext"]'),
                key: $viewAccountModal.querySelector('[data-field="key"]'),
            },
            $actionsContainer: $viewAccountModal.querySelector<HTMLElement>('[data-actions-container]'),
            $regenKeyBtn: $viewAccountModal.querySelector<HTMLButtonElement>('[data-regen-key-button]'),
            fetchErrorMessage: 'Erreur de chargement',
        };

        // Prise en charge des boutons à l'intérieur d'un contenu Tippy
        const that = this;
        document.querySelectorAll<PopperElement>('[data-tippy]').forEach($tippy => {
            if (typeof $tippy._tippy === 'undefined') {
                return;
            }

            const onMountParent = $tippy._tippy.props.onMount;
            $tippy._tippy.setProps({
                onMount(instance) {
                    if (true !== (instance.reference as InitializableHTMLElement<Props>).viewAccountInitialized) {
                        onMountParent(instance);

                        (instance.reference as InitializableHTMLElement<Props>).viewAccountInitialized = true;
                        that.initButtonsAttr(instance.popper as HTMLElement);
                    }
                },
            });
        });

        // Prise en charge des boutons présents au chargement de la page
        that.initButtonsAttr(document);

        this.viewModalAttr.$regenKeyBtn?.addEventListener('click', evt => {
            evt.preventDefault();
            this.regenKey();
        });
    }

    /**
     * Recherche et initialisation des boutons d'ouverture de la viewAccountModal
     * 
     * @param {HTMLElement|Document} $parent Parent dans lequel faire la recherche
     * 
     * @return {void}
     */
    initButtonsAttr($parent: HTMLElement|Document): void {
        const buttonsAttr = Array.from($parent.querySelectorAll<HTMLButtonElement>('.js-view-account-button')).map($button => {
            const refExt = $button.dataset.refExt ?? '';
            let name = $button.dataset.name ?? '';
            let domains = $button.dataset.domains ?? '';
            let status = $button.dataset.status ?? '';
            let key = $button.dataset.key ?? '';

            // Un buttonAttr concernant cet Account existe peut-être déjà. Dans ce cas, on récupère ses valeurs
            this.buttonsAttr.filter(btnAttr => btnAttr.refExt === refExt).forEach((btnAttr, index) => {
                if (index !== 0) {
                    return;
                }

                name = btnAttr.name ?? name;
                domains = btnAttr.domains ?? domains;
                status = btnAttr.status ?? status;
                key = btnAttr.key ?? status;
            });

            return { $button, refExt, name, domains, status, key };
        }).filter(buttonAttr => buttonAttr.refExt);

        buttonsAttr.forEach(buttonAttr => {
            buttonAttr.$button.addEventListener('click', evt => {
                evt.preventDefault();
                this.showViewModal(buttonAttr);
            });
        });

        this.buttonsAttr = [...this.buttonsAttr, ...buttonsAttr];
    }

    /**
     * Ouverture de la viewAccountModal et affichage des informations de l'Account à l'intérieur
     * 
     * @param {ButtonAttrType} buttonAttr
     * 
     * @return {Promise<void>}
     */
    async showViewModal(buttonAttr: ButtonAttrType): Promise<void> {
        this.cleanViewModal();

        this.openedButtonAttr = buttonAttr;

        // Ouverture de la modal
        this.viewModalAttr.$modal.open();

        // Affichage des valeurs
        this.displayAccountFields(buttonAttr);

        // Récupération et affichage des valeurs manquantes
        if (!this.isButtonAttrInitialized(buttonAttr)) {
            await this.populateButtonAttr(buttonAttr).then(success => {
                this.displayAccountFields(buttonAttr, !success);
            });
        }
    }

    /**
     * Clean des informations de la viewAccountModal
     * 
     * @param {AccountFieldType[]} fields [default: all]
     * 
     * @return {void}
     */
    cleanViewModal(fields: AccountFieldType[] = ['refExt', 'name', 'domains', 'status', 'key']): void {
        fields.forEach(field => {
            if (!this.viewModalAttr.$detailContainer[field]) {
                return;
            }

            (this.viewModalAttr.$detailContainer[field] as HTMLElement).innerHTML = '&nbsp;';
            (this.viewModalAttr.$detailContainer[field] as HTMLElement).classList.add('text-loading');
        });
    }

    /**
     * Détermine si toutes les informations d'un Account sont initialisées
     * 
     * @param {ButtonAttrType} buttonAttr
     * 
     * @return {boolean}
     */
    isButtonAttrInitialized(buttonAttr: ButtonAttrType): boolean {
        return !!(buttonAttr.name && buttonAttr.domains && buttonAttr.status && buttonAttr.key);
    }

    /**
     * Récupère les informations d'un Account en ajax
     * 
     * @param {ButtonAttrType} buttonAttr
     * 
     * @return {Promise<boolean>}
     */
    async populateButtonAttr(buttonAttr: ButtonAttrType): Promise<boolean> {
        return getData(scriptVar.ajaxUrl, {
            action: 'get_acme_account_values',
            ref_ext: buttonAttr.refExt,
            values: ['name', 'domains_name', 'status', 'key_key'].join(','),
        }).then(response => {
            if (response.success) {
                // Un autre buttonAttr concernant cet Account existe peut-être. Dans ce cas, on met également à jour ses valeurs
                this.buttonsAttr.filter(btnAttr => btnAttr.refExt === buttonAttr.refExt).forEach(btnAttr => {
                    btnAttr.name = response.data.name;
                    btnAttr.domains = response.data.domains.join(', ');
                    btnAttr.status = response.data.status;
                    btnAttr.key = response.data.key;
                });

                return true;
            }

            return false;
        }).catch(() => false);
    }

    /**
     * Affiche les informations d'un Account à l'intérieur de la viewAccountModal
     * 
     * @param {ButtonAttrType} buttonAttr
     * @param {boolean} fakeValues Si true, affiche un message d'erreur à la place de la valeur si celle-ci n'a pas pu être récupérée
     * 
     * @return {void}
     */
    displayAccountFields(buttonAttr: ButtonAttrType, fakeValues: boolean = false): void {
        if (!fakeValues && this.openedButtonAttr !== buttonAttr) {
            return;
        }

        (['refExt', 'name', 'domains', 'status', 'key'] as AccountFieldType[]).forEach(field => {
            let value = buttonAttr[field];

            if (!value) {
                if (!fakeValues) {
                    return;
                }

                // Si fakeValues est à true et que la valeur est null, on affiche un message d'erreur
                value = this.viewModalAttr.fetchErrorMessage;
            }

            this.displayAccountField(field, value);
        });
    }

    /**
     * Affiche une information d'un Account à l'intérieur de la viewAccountModal
     * 
     * @param {AccountFieldType} field
     * @param {string} value
     * 
     * @return {void}
     */
    displayAccountField(field: AccountFieldType, value: string): void {
        if (!this.viewModalAttr.$detailContainer[field]) {
            return;
        }

        (this.viewModalAttr.$detailContainer[field] as HTMLElement).innerText = value;
        (this.viewModalAttr.$detailContainer[field] as HTMLElement).classList.remove('text-loading');
    }

    /**
     * Demande le régénération d'une clé d'un Account puis affiche la valeur
     * 
     * @return {Promise<void>}
     */
    async regenKey(): Promise<void> {
        const buttonAttr = this.openedButtonAttr;

        if (!buttonAttr) {
            return;
        }

        this.cleanViewModal(['key']);
        const success = await this.askNewKey(buttonAttr);
        if (this.openedButtonAttr === buttonAttr) {
            this.displayAccountField('key', success && buttonAttr.key ? buttonAttr.key : this.viewModalAttr.fetchErrorMessage);
        }
    }

    /**
     * Demande la régénération d'une clé d'un Account en ajax
     * 
     * @param {ButtonAttrType} buttonAttr
     * 
     * @return {Promise<boolean>}
     */
    async askNewKey(buttonAttr: ButtonAttrType): Promise<boolean> {
        return postData(scriptVar.ajaxUrl, {
            action: 'regen_account_key',
            ref_ext: buttonAttr.refExt,
        }).then(response => {
            if (response.success) {
                // Un autre buttonAttr concernant cet Account existe peut-être. Dans ce cas, on met également à jour ses valeurs
                this.buttonsAttr.filter(btnAttr => btnAttr.refExt === buttonAttr.refExt).forEach(btnAttr => {
                    btnAttr.key = response.data;
                });

                return true;
            }

            return false;
        }).catch(() => false);
    }
}
